import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LandingPageSection3.module.css';
import img1 from '../../../../Images/avatar image/01.png';
import img2 from '../../../../Images/avatar image/02.png';
import img3 from '../../../../Images/avatar image/03.png';
import img4 from '../../../../Images/avatar image/04.png';
import img5 from '../../../../Images/avatar image/05.png';
import img6 from '../../../../Images/avatar image/06.png';

// Import your voice samples (replace with real .mp3 files)
import voice1 from '../../../../RecordedVoices/realEstate.wav';
import voice2 from '../../../../RecordedVoices/lead.wav';
import voice3 from '../../../../RecordedVoices/hospital.wav';
import voice4 from '../../../../RecordedVoices/assistent.wav';
import voice5 from '../../../../RecordedVoices/customer.wav';
import voice6 from '../../../../RecordedVoices/support.wav';
import { FaMicrophone, FaPause, FaPlay } from 'react-icons/fa';
import { MdMacroOff, MdMicOff } from 'react-icons/md';

const aiAgents = [
  {
    agentId: 1,
    imageUrl: img1,
    name: 'Sarah Johnson',
    role: 'Real Estate Specialist',
    description: 'Guides clients through property buying and selling with personalized recommendations.',
    color: '#ffdfbf',
    voiceSample: voice1,
  },
  {
    agentId: 2,
    imageUrl: img2,
    name: 'Rohit Singh',
    role: 'Lead Generation Expert',
    description: 'Drives high-quality leads with intelligent outreach and automated follow-ups.',
    color: '#b8e6b8',
    voiceSample: voice2,
  },
  {
    agentId: 3,
    imageUrl: img3,
    name: 'Aileen Kim',
    role: 'Hospital Receptionist',
    description: 'Manages patient appointments, inquiries, and reminders with care.',
    color: '#c2e0ff',
    voiceSample: voice3,
  },
  {
    agentId: 4,
    imageUrl: img4,
    name: 'David Lopez',
    role: 'AI Calling Assistant',
    description: 'Drives high-quality leads with intelligent outreach and automated follow-ups.',
    color: '#e6d8ff',
    voiceSample: voice4,
  },
  {
    agentId: 5,
    imageUrl: img5,
    name: 'Chen Wu',
    role: 'Customer Success AI',
    description: 'Ensures follow-ups and customer satisfaction, tracks feedback automatically.',
    color: '#fff4b8',
    voiceSample: voice5,
  },
  {
    agentId: 6,
    imageUrl: img6,
    name: 'Priya Desai',
    role: 'AI Calling Support',
    description: 'Manages patient appointments, inquiries, and reminders with care.',
    color: '#ffd1dc',
    voiceSample: voice6,
  },
];

const LandingPageSection3 = ({ selectedAgent, setSelectedAgent }) => {
  const navi = useNavigate();

  const [playingAgentId, setPlayingAgentId] = useState(null);
  const audioRef = useRef(null);
  const currentAudioRef = useRef(null);

  useEffect(() => {
    if (!selectedAgent) {
      setSelectedAgent(aiAgents[0]);
    }
  }, [selectedAgent, setSelectedAgent]);

  const currentAgent = selectedAgent || aiAgents[0];

  const handleAgentClick = useCallback((agent) => {
    setSelectedAgent(agent);
  }, [setSelectedAgent]);

  const toggleAudio = (agent) => {
    if (playingAgentId === agent.agentId) {
      // Pause current
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setPlayingAgentId(null);
    } else {
      // Stop any previous audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }

      const newAudio = new Audio(agent.voiceSample);
      newAudio.play();

      currentAudioRef.current = newAudio;
      setPlayingAgentId(agent.agentId);

      newAudio.onended = () => {
        setPlayingAgentId(null);
        currentAudioRef.current = null;
      };
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          Meet Your AI Team — <br /> Smart Agents Tailored to Your Business
        </h2>
        <p className={styles.subtitle}>
          Our specialized AI agents handle calls, leads, and customer interactions with precision, efficiency, and human-like intelligence.
        </p>

        <div className={styles.grid}>
          {aiAgents.map((agent, index) => {
            const isSelected = currentAgent.agentId === agent.agentId;
            const isPlaying = playingAgentId === agent.agentId;

            return (
              <div
                key={agent.agentId}
                className={`${styles.card} ${isSelected ? styles.selected : ''}`}
                style={{ animationDelay: `${0.8 + index * 0.15}s` }}
                onClick={() => handleAgentClick(agent)}
              >
                {/* Glowing Orb Avatar */}
                <div className={styles.avatarWrapper}>
                  <div
                    className={styles.glowOrb}
                    style={{ backgroundColor: agent.color }}
                  >
                    <img src={agent.imageUrl} alt={agent.name} className={styles.avatarImg} />
                  </div>
                </div>

                <div className={styles.cardHeader}>
                  <div>
                    <h3 className={styles.agentRole}>{agent.role}</h3>
                    <p className={styles.agentName}>{agent.name}</p>
                  </div>
                </div>

                {/* Voice Button + Animated Bars */}
                <div
                  className={`${styles.voiceControl} ${isPlaying ? styles.playing : ''}`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card selection
                    toggleAudio(agent);
                  }}
                >
                  <div className={styles.soundBars}>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className={styles.micIcon}>
                    {isPlaying ?  <FaPause /> : <FaPlay/> }
                  </span>
                </div>

                <p className={styles.description}>{agent.description}</p>

                {isSelected && <div className={styles.activeRing}></div>}
              </div>
            );
          })}
        </div>

        <div className={styles.ctaContainer}>
          <p className={styles.ctaText}>Let Your AI Team Work for You 24/7</p>
          <button className={styles.ctaButton} onClick={() => navi('/voices')}>
            Explore More
            <span className={styles.arrow}>→</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default LandingPageSection3;